import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

const Footer = ({ showDisclaimer = true }) => {
  const navigate = useNavigate();

  const handleHomeClick = (e) => {
    e.preventDefault();
    navigate('/');
    window.scrollTo(0, 0);
  };

  return (
    <footer className="border-t border-slate-200 bg-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" onClick={handleHomeClick} className="flex items-center gap-2 mb-4" data-testid="footer-logo-link">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
            </Link>
            <p className="text-sm text-slate-600 mb-4">
              The sponsorship search engine for creators. Discover brands actively sponsoring in your niche.
            </p>
          </div>

          {/* Platform Links */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Platform</h3>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/" 
                  onClick={handleHomeClick}
                  className="text-sm text-slate-600 hover:text-indigo-600 transition-colors"
                  data-testid="footer-home-link"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link to="/explore-brands" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">
                  Brands
                </Link>
              </li>
              <li>
                <Link to="/leaderboards" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">
                  Leaderboards
                </Link>
              </li>
              <li>
                <Link to="/brands/login" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">
                  For Brands
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Legal</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/terms" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">Contact</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href="mailto:hello.sponsiq@gmail.com" 
                  className="text-sm text-slate-600 hover:text-indigo-600 transition-colors"
                  data-testid="footer-contact-email"
                >
                  hello.sponsiq@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Disclaimer */}
        {showDisclaimer && (
          <div className="mt-8 pt-8 border-t border-slate-100">
            <p className="text-xs text-slate-500 leading-relaxed max-w-4xl">
              <strong>Disclaimer:</strong> Sponsorship data is inferred from publicly available YouTube content and is intended for informational purposes only. Sponsiq does not represent or guarantee official brand partnerships.
            </p>
          </div>
        )}

        {/* Copyright */}
        <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Sponsiq. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-xs text-slate-500 hover:text-indigo-600 transition-colors">
              Terms
            </Link>
            <Link to="/privacy" className="text-xs text-slate-500 hover:text-indigo-600 transition-colors">
              Privacy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Compact disclaimer for reports and dashboard pages
export const PlatformDisclaimer = () => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-8">
    <p className="text-xs text-slate-600 leading-relaxed">
      <strong>Data Disclaimer:</strong> Sponsorship data is inferred from publicly available YouTube content and is intended for informational purposes only. Sponsiq does not represent or guarantee official brand partnerships.
    </p>
  </div>
);

export default Footer;
